/* Figures the page derives from what the two endpoints return. Money comes from
   /statistics (ledgers + platform earnings); the AI's activity from its own
   counters (/chat-stats). AI revenue is the AI's Seller share — never the
   counters' PPV/tip sums (CONTEXT.md "AI revenue"). */

import type { ChatStatsAccount, ChatStatsTotals, StatisticsResponse, StatisticsTeamAccount, StatisticsTeamRow } from '../../../api/client';
import { ledgerMoney, pick, ratio, type MoneyMode } from './model';

export interface AiActivity {
  chats: number;
  newChats: number;
  messages: number;
  responses: number;
  asksHot: number;
  asksNormal: number;
  asksPaidSexting: number;
  /** Tip asks only (hot + normal + paid sexting). */
  asks: number;
  /** Every time the AI asked for money: tip asks, PPVs sent, customs and calls it
      handled. The fan path's middle step, so each kind of payment has its ask. */
  moneyAsks: number;
  ppvSentLadder: number;
  ppvSentRequests: number;
  ppvSent: number;
  customsHandled: number;
  customsPaid: number;
  vcHandled: number;
  vcPaid: number;
  vipSent: number;
  /** Things a fan paid for, as the AI's counters saw them (the fan path). */
  payments: number;
}

export function aiActivity(t: ChatStatsTotals | ChatStatsAccount | undefined): AiActivity {
  const n = (k: keyof ChatStatsTotals) => Number(t?.[k] ?? 0);
  const asksHot = n('tip_asks_hot');
  const asksNormal = n('tip_asks_normal');
  const asksPaidSexting = n('tip_asks_paid_sexting');
  const ppvSentLadder = n('ppv_sent_sales_ladder');
  const ppvSentRequests = n('ppv_sent_content_request');
  const asks = asksHot + asksNormal + asksPaidSexting;
  const ppvSent = ppvSentLadder + ppvSentRequests;
  const customsHandled = n('custom_requests_processed');
  const vcHandled = n('vc_requests_processed');
  return {
    chats: n('chats_activated'),
    newChats: n('new_chats'),
    messages: n('messages_sent') + n('follow_ups_sent'),
    responses: n('turns'),
    asksHot,
    asksNormal,
    asksPaidSexting,
    asks,
    moneyAsks: asks + ppvSent + customsHandled + vcHandled,
    ppvSentLadder,
    ppvSentRequests,
    ppvSent,
    customsHandled,
    customsPaid: n('custom_requests_paid'),
    vcHandled,
    vcPaid: n('vc_requests_paid'),
    vipSent: n('vip_offers_sent'),
    payments: n('tips_received_count') + n('ppv_purchased_count') + n('custom_requests_paid') + n('vc_requests_paid'),
  };
}

export interface AiMoney {
  revenue: number;
  ppv: number;
  tips: number;
  ppvSold: number;
  tipsCount: number;
}

/** The AI's Seller share, split into PPVs and tips, in the page's money mode. */
export function aiMoney(stats: StatisticsResponse | null, mode: MoneyMode): AiMoney {
  const m = stats?.money;
  const ppvGross = m?.ppv_sold.ai.gross ?? 0;
  const totalGross = m?.sellers.ai.gross ?? 0;
  const days = m?.days ?? [];
  const ppvSold = days.reduce((a, d) => a + d.ppv_sold.ai, 0);
  const payments = days.reduce((a, d) => a + d.payments.ai, 0);
  return {
    revenue: pick(m?.sellers.ai, mode),
    ppv: ledgerMoney(ppvGross, mode),
    tips: ledgerMoney(Math.max(0, totalGross - ppvGross), mode),
    ppvSold,
    tipsCount: Math.max(0, payments - ppvSold),
  };
}

export interface TeamTotals {
  sales: number;
  ppvSent: number;
  ppvSold: number;
  messages: number;
  chats: number;
  tips: number;
  ppvSales: number;
}

/** The Team's totals. Chats count each fan once per account even when several
    operators wrote to them (``accounts``); per-operator rows would count it twice. */
export function teamTotals(rows: StatisticsTeamRow[], mode: MoneyMode, accounts: StatisticsTeamAccount[] = []): TeamTotals {
  const sum = (k: keyof StatisticsTeamRow) => rows.reduce((a, r) => a + Number(r[k] || 0), 0);
  return {
    sales: ledgerMoney(sum('total_sales'), mode),
    tips: ledgerMoney(sum('tips'), mode),
    ppvSales: ledgerMoney(sum('ppv_sales'), mode),
    ppvSent: sum('ppv_sent'),
    ppvSold: sum('ppv_sold'),
    messages: sum('total_messages'),
    chats: accounts.length ? accounts.reduce((a, x) => a + x.chats, 0) : sum('chats_count'),
  };
}

export const perChat = (money: number, chats: number) => ratio(money, chats);
